import { Link } from 'react-router-dom'

import { useDataQuality } from '../../lib/admin'
import type { SlotCoverage } from '../../lib/admintypes'
import { count } from '../../lib/format'

import { AdminLayout } from './AdminLayout'

/**
 * Data quality (06 §9).
 *
 * The question this screen answers is not "how many products do we have" — it is **"can the
 * studio actually do its job right now?"** A slot with nothing approved behind it is a slot
 * the agent has to refuse, and a homeowner tapping a vanity and being told there are no
 * vanities is the worst thirty seconds this product has.
 *
 * So the numbers that lead are coverage and price age.
 *
 * **One list, in the order things need doing.** This screen used to print the slot registry
 * twice — 68 empty slots as a cloud of chips, then all 89 again in a table where 68 rows read
 * zero, zero. Two screens of scrolling to learn one thing. The three groups below are the three
 * different situations a slot can be in, and only the first is a problem you can fix today:
 *
 *  * **Approved but not sellable** — somebody whitelisted these and they still cannot appear.
 *    Almost always a price older than seven days (06 §1), which one crawl fixes.
 *  * **Live** — working. Sorted by how thin the rail is, because three products is a rail that
 *    will disappoint before it fails.
 *  * **Nothing yet** — collapsed. A slot with no products is not a fault; ASB does not sell
 *    every category in the registry, and 68 chips shouting about it is noise, not information.
 */

export function AdminOverview() {
  const quality = useDataQuality()
  const data = quality.data

  return (
    <AdminLayout>
      <h1 className="text-2xl">Overview</h1>

      {quality.isPending && <p className="mt-6 text-muted">Loading…</p>}

      {data && (
        <>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="Approved products"
              value={count(data.products_whitelisted)}
              hint={`of ${count(data.products_total)} crawled`}
            />
            <Stat
              label="Slots with products"
              value={`${data.slot_coverage.filter((slot) => slot.with_price > 0).length}`}
              hint={`of ${data.slot_coverage.length} in the catalogue`}
            />
            <Stat
              label="Prices under a day old"
              value={`${data.price_age_buckets.today ?? 0}`}
              hint={`${data.price_age_buckets.stale ?? 0} over seven days`}
            />
            <Stat
              label="With a photo"
              value={`${data.with_image_pct}%`}
              hint={`${data.with_size_pct}% have a size`}
            />
          </div>

          {(data.price_age_buckets.stale ?? 0) > 0 && (
            <p className="mt-4 rounded-card border border-line bg-sand px-4 py-3 text-sm text-ink">
              <strong>{data.price_age_buckets.stale}</strong> approved products have prices over
              seven days old. They are hidden from homeowners until a crawl refreshes them —{' '}
              <Link to="/admin/stores" className="underline">
                run a price refresh
              </Link>
              .
            </p>
          )}

          <Coverage rows={data.slot_coverage} />
        </>
      )}
    </AdminLayout>
  )
}

/** How thin a live rail can get before it is worth saying so out loud. */
const THIN_RAIL = 5

function Coverage({ rows }: { rows: SlotCoverage[] }) {
  const unsellable = rows
    .filter((slot) => slot.whitelisted > 0 && slot.with_price === 0)
    .sort((a, b) => b.whitelisted - a.whitelisted)
  const live = rows
    .filter((slot) => slot.with_price > 0)
    .sort((a, b) => a.with_price - b.with_price)
  const empty = rows.filter((slot) => slot.whitelisted === 0 && slot.with_price === 0)

  return (
    <>
      {unsellable.length > 0 && (
        <>
          <h2 className="mt-9 text-xl">Approved, but a homeowner cannot see them</h2>
          <p className="mt-1 max-w-[70ch] text-sm text-muted">
            These have products you approved and none the studio can offer — nearly always a price
            over seven days old.{' '}
            <Link to="/admin/stores" className="underline">
              A price refresh
            </Link>{' '}
            fixes it.
          </p>
          <SlotTable rows={unsellable} tone="warn" />
        </>
      )}

      <h2 className="mt-9 text-xl">Live in the studio</h2>
      {live.length === 0 ? (
        <p className="mt-3 rounded-card border border-line bg-white p-5 text-muted">
          Nothing is live yet. Approve some products and the rails fill up.
        </p>
      ) : (
        <>
          <p className="mt-1 max-w-[70ch] text-sm text-muted">
            Thinnest first — a rail with {THIN_RAIL} products will disappoint somebody before it
            fails.
          </p>
          <SlotTable rows={live} tone="live" />
        </>
      )}

      {empty.length > 0 && (
        <details className="mt-6">
          <summary className="cursor-pointer text-sm font-semibold text-ink">
            {empty.length} slots with nothing behind them
          </summary>
          <p className="mt-2 max-w-[70ch] text-sm text-muted">
            The studio will not offer these, which is the right behaviour — a slot with no products
            is better than a slot with bad ones. ASB does not sell every category in the registry.
          </p>
          <ul className="m-0 mt-3 flex list-none flex-wrap gap-2 p-0">
            {empty.map((slot) => (
              <li key={slot.slot}>
                <Link
                  to={`/admin/products?slot=${slot.slot}`}
                  className="inline-block min-h-11 rounded-pill border border-line bg-white px-3.5 text-sm leading-[2.4] text-ink hover:border-blue"
                >
                  {slot.name}
                </Link>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  )
}

function SlotTable({ rows, tone }: { rows: SlotCoverage[]; tone: 'warn' | 'live' }) {
  return (
    <div className="mt-3 overflow-x-auto rounded-card border border-line bg-white">
      <table className="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-line text-left">
            {['Slot', 'Room', 'Approved', 'In the studio'].map((heading) => (
              <th
                key={heading}
                className="p-3 font-mono text-xs uppercase tracking-[0.1em] text-muted"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((slot) => (
            <tr key={slot.slot} className="border-b border-line last:border-0">
              <td className="p-3">
                <Link
                  to={`/admin/products?slot=${slot.slot}`}
                  className="text-ink underline-offset-2 hover:underline"
                >
                  {slot.name}
                </Link>
              </td>
              <td className="p-3 text-muted">{slot.room}</td>
              <td className="p-3 tabular-nums text-muted">{slot.whitelisted}</td>
              <td
                className={`p-3 font-semibold tabular-nums ${
                  tone === 'warn'
                    ? 'text-warn'
                    : slot.with_price < THIN_RAIL
                      ? 'text-warn'
                      : 'text-good'
                }`}
              >
                {slot.with_price}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-card border border-line bg-white p-4">
      <p className="m-0 font-mono text-xs uppercase tracking-[0.12em] text-muted">{label}</p>
      <p className="m-0 mt-1 font-display text-3xl leading-none text-ink">{value}</p>
      <p className="m-0 mt-1.5 text-xs text-muted">{hint}</p>
    </div>
  )
}
